// Rule metadata registry — single source of truth for all challenge rule types.
//
// Each entry maps a backend rule_type string to its UI configuration:
// label, category, valid options, and default values. When the backend
// adds a new rule type, update this file and add a param renderer.

import type { Authority } from '../form';

export type RuleCategory = 'risk_limits' | 'trading_restrictions';

export interface RuleTypeConfig {
  key: string;
  label: string;
  description: string;
  category: RuleCategory;
  requiresValue: boolean;
  defaultValueMode: string | null;
  validValueModes: string[];
  defaultTiming: string;
  /** The rung this family's first occurrence costs, as a firm ordinarily sells
   *  it. An author changes it; nothing fills it in behind them. */
  defaultAction: string;
  /** Whether this rule type has rule-specific params beyond the common fields. */
  hasParams: boolean;
  /** Handwritten mirror of RuleType::supports_scoping, used for editor scope visibility. */
  scopable?: boolean;
  /** Whether this rule type is implemented in the engine (compiler + enforcement). */
  implemented: boolean;
  /** Set when the rule is implemented but depends on a data feed the platform
   *  does not yet produce. The engine checks the rule faithfully against an
   *  empty feed, so an ENFORCING publish is refused by the publish gate — see
   *  `challenge_rule_type_vocabulary.requires_data_feed`. This is not
   *  `implemented: false`: the code exists and warn mode works today. */
  availabilityNote?: string;
  /** Common rules show by default; advanced rules are behind a toggle. */
  common: boolean;
  /** Rules handled by inline fields in the core step settings.
   *  These are NOT shown in the StepRulesEditor toggle list because they
   *  have dedicated UI (Max Daily Loss %, Max Drawdown %, Drawdown Type).
   *  Showing them as toggles too would create confusion and allow
   *  conflicting configurations (e.g., both total AND trailing DD enabled). */
  coreInline: boolean;
}

// ── Timezone options ─────────────────────────────────────────────────

export const TIMEZONE_OPTIONS = [
  { value: 'America/New_York', label: 'Eastern Time (New York)' },
  { value: 'America/Chicago', label: 'Central Time (Chicago)' },
  { value: 'America/Denver', label: 'Mountain Time (Denver)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (Los Angeles)' },
  { value: 'America/Toronto', label: 'Toronto (ET)' },
  { value: 'America/Sao_Paulo', label: 'São Paulo (BRT)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Dublin', label: 'Dublin (GMT/IST)' },
  { value: 'Europe/Lisbon', label: 'Lisbon (WET)' },
  { value: 'Europe/Berlin', label: 'Central Europe (Berlin)' },
  // A preset day boundary the platform already ships on (migration 629). A
  // zone the list does not name is a zone the author cannot pick back after a
  // copy, so every zone in the catalogue belongs here.
  { value: 'Europe/Prague', label: 'Prague (CET/CEST)' },
  { value: 'Europe/Warsaw', label: 'Warsaw (CET/CEST)' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'Europe/Madrid', label: 'Madrid (CET/CEST)' },
  { value: 'Europe/Rome', label: 'Rome (CET/CEST)' },
  { value: 'Europe/Zurich', label: 'Zurich (CET/CEST)' },
  { value: 'Europe/Athens', label: 'Athens (EET/EEST)' },
  { value: 'Europe/Istanbul', label: 'Istanbul (TRT)' },
  { value: 'Europe/Moscow', label: 'Moscow' },
  { value: 'Africa/Johannesburg', label: 'Johannesburg (SAST)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Asia/Kolkata', label: 'India (IST)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Asia/Shanghai', label: 'China (CST)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Seoul', label: 'Seoul (KST)' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong (HKT)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
  { value: 'Pacific/Auckland', label: 'New Zealand (NZST)' },
  { value: 'UTC', label: 'UTC' },
  { value: 'Etc/UTC', label: 'UTC (Etc)' },
] as const;

export const RULE_CATEGORIES: Array<{ key: RuleCategory; label: string; description: string }> = [
  {
    key: 'risk_limits',
    label: 'Risk Limits',
    description: 'Capital protection — guards that monitor equity, drawdown, and exposure.',
  },
  {
    key: 'trading_restrictions',
    label: 'Trading Restrictions',
    description: 'Behavioral constraints — control when, what, and how the trader can trade.',
  },
];

const CONSEQUENCES = [
  { value: 'none', label: 'No Action' },
  { value: 'warn_trader', label: 'Warn Trader' },
  { value: 'flag_for_review', label: 'Flag for Review' },
  { value: 'reject_order', label: 'Reject Order' },
  { value: 'breach_account', label: 'Breach Account' },
  { value: 'suspend_account', label: 'Suspend Account' },
  { value: 'close_positions', label: 'Close Positions' },
  { value: 'pause_until_next_period', label: 'Pause Until the Next Day' },
  { value: 'void_trade_profit', label: 'Void Trade Profit' },
] as const;

// Pinned through real Rust constructors by ruleAuthoring.acceptance.test.ts
// and display::tests::editor_payloads_match_constructor_acceptance.
export function consequencesForTiming(timing: string, ruleType: string) {
  const config = RULE_REGISTRY[ruleType];
  if (!config?.implemented) return [];
  const extraTiming = (ruleType === 'news_trading' && timing === 'post_trade')
    || (['max_intended_risk_per_trade', 'max_aggregate_intended_risk'].includes(ruleType)
      && timing === 'continuous');
  if (timing !== config.defaultTiming && !extraTiming) return [];
  return CONSEQUENCES.filter(({ value }) => {
    if (timing === 'scheduled' && ruleType === 'flat_by_schedule') {
      return value === 'close_positions' || value === 'reject_order';
    }
    if (timing === 'post_trade') return ['none', 'warn_trader', 'flag_for_review', 'void_trade_profit', 'breach_account'].includes(value);
    if (['none', 'warn_trader', 'flag_for_review'].includes(value)) return true;
    if (timing === 'pre_trade') return value === 'reject_order';
    return ['breach_account', 'suspend_account', 'close_positions'].includes(value)
      || (ruleType === 'max_daily_loss' && value === 'pause_until_next_period');
  });
}

/** What the builder offers this author. A person's own template runs itself,
 *  so it offers nothing that waits for the firm: the server refuses a review
 *  flag and a suspension from anyone but the firm. */
export function authorConsequences(timing: string, ruleType: string, authority: Authority) {
  const all = consequencesForTiming(timing, ruleType);
  return authority === 'firm' ? all : all.filter(({ value }) => value !== 'flag_for_review' && value !== 'suspend_account');
}

export function countsRuleOccurrences(timing: string): boolean {
  return ['pre_trade', 'continuous', 'post_trade'].includes(timing);
}

export const NEWS_ARMS = [
  { value: 'admission', label: 'Refuse orders opened inside (pre-trade)', timing: 'pre_trade' },
  { value: 'opened_inside', label: 'Exposure opened inside (judged)', timing: 'post_trade' },
] as const;

export const NEWS_UNAVAILABLE_NOTICE =
  'Closed-inside and held-through news rules cannot be published because their judgment is unavailable.';

// ── Registry ─────────────────────────────────────────────────────────

export const RULE_REGISTRY: Record<string, RuleTypeConfig> = {
  // ── Risk Limits (continuous) ──
  max_daily_loss: {
    key: 'max_daily_loss',
    label: 'Max Daily Loss',
    description: 'Maximum equity loss allowed in a single trading day.',
    category: 'risk_limits',
    requiresValue: true,
    defaultValueMode: 'percent',
    validValueModes: ['percent', 'absolute'],
    defaultTiming: 'continuous',
    defaultAction: 'breach_account',
    hasParams: true,
    implemented: true,
    common: true,
    coreInline: true,
  },
  max_total_drawdown: {
    key: 'max_total_drawdown',
    label: 'Max Total Drawdown',
    description: 'Maximum drawdown from initial balance.',
    category: 'risk_limits',
    requiresValue: true,
    defaultValueMode: 'percent',
    validValueModes: ['percent', 'absolute'],
    defaultTiming: 'continuous',
    defaultAction: 'breach_account',
    hasParams: true,
    implemented: true,
    common: true,
    coreInline: true,
  },
  max_trailing_drawdown: {
    key: 'max_trailing_drawdown',
    label: 'Max Trailing Drawdown',
    description: 'Maximum drawdown from the highest peak balance/equity.',
    category: 'risk_limits',
    requiresValue: true,
    defaultValueMode: 'percent',
    validValueModes: ['percent', 'absolute'],
    defaultTiming: 'continuous',
    defaultAction: 'breach_account',
    hasParams: true,
    implemented: true,
    common: true,
    coreInline: true,
  },
  max_risk_per_trade: {
    key: 'max_risk_per_trade',
    // Matches the objective card's wording: an author picking this rule and a
    // trader reading its card must be looking at the same name.
    label: 'Max Open Loss Per Trade',
    description: 'Maximum unrealized loss allowed on any single position.',
    category: 'risk_limits',
    requiresValue: true,
    defaultValueMode: 'percent',
    validValueModes: ['percent', 'absolute'],
    defaultTiming: 'continuous',
    defaultAction: 'breach_account',
    hasParams: true,
    implemented: true,
    common: false,
    coreInline: false,
  },
  max_total_unrealized_loss: {
    key: 'max_total_unrealized_loss',
    scopable: true,
    label: 'Max Total Unrealized Loss',
    description: 'Maximum combined unrealized loss across all open positions.',
    category: 'risk_limits',
    requiresValue: true,
    defaultValueMode: 'percent',
    validValueModes: ['percent', 'absolute'],
    defaultTiming: 'continuous',
    defaultAction: 'breach_account',
    hasParams: true,
    implemented: true,
    common: false,
    coreInline: false,
  },

  // ── Risk Limits (pre-trade) ──
  max_position_size: {
    key: 'max_position_size',
    scopable: true,
    label: 'Max Position Size',
    description: 'Maximum lots per position (resulting size, not order size).',
    category: 'risk_limits',
    requiresValue: true,
    defaultValueMode: 'lots',
    validValueModes: ['lots'],
    defaultTiming: 'pre_trade',
    defaultAction: 'reject_order',
    hasParams: false,
    implemented: true,
    common: true,
    coreInline: false,
  },
  max_total_exposure: {
    key: 'max_total_exposure',
    scopable: true,
    label: 'Max Total Exposure',
    description: 'Maximum total lots across all open positions.',
    category: 'risk_limits',
    requiresValue: true,
    defaultValueMode: 'lots',
    validValueModes: ['lots'],
    defaultTiming: 'pre_trade',
    defaultAction: 'reject_order',
    hasParams: true,
    implemented: true,
    common: false,
    coreInline: false,
  },
  max_concurrent_positions: {
    key: 'max_concurrent_positions',
    scopable: true,
    label: 'Max Concurrent Positions',
    description: 'Maximum number of positions open at the same time.',
    category: 'risk_limits',
    requiresValue: true,
    defaultValueMode: 'absolute',
    validValueModes: ['absolute'],
    defaultTiming: 'pre_trade',
    defaultAction: 'reject_order',
    hasParams: false,
    implemented: true,
    common: false,
    coreInline: false,
  },
  max_leverage: {
    key: 'max_leverage',
    scopable: true,
    label: 'Max Leverage',
    description: 'Maximum effective leverage (notional / equity).',
    category: 'risk_limits',
    requiresValue: true,
    defaultValueMode: 'absolute',
    validValueModes: ['absolute'],
    defaultTiming: 'pre_trade',
    defaultAction: 'reject_order',
    hasParams: false,
    implemented: true,
    common: true,
    coreInline: false,
  },

  // ── Trading Restrictions ──
  trading_hours: {
    key: 'trading_hours',
    label: 'Trading Hours',
    description: 'Restrict trading to specific hours of the day.',
    category: 'trading_restrictions',
    requiresValue: false,
    defaultValueMode: null,
    validValueModes: [],
    defaultTiming: 'pre_trade',
    defaultAction: 'reject_order',
    hasParams: true,
    implemented: true,
    common: false,
    coreInline: false,
  },
  weekend_holding: {
    key: 'weekend_holding',
    label: 'Weekend Holding',
    description: 'Force-close positions before the weekend.',
    category: 'trading_restrictions',
    requiresValue: false,
    defaultValueMode: null,
    validValueModes: [],
    defaultTiming: 'scheduled',
    defaultAction: 'close_positions',
    hasParams: true,
    implemented: true,
    common: false,
    coreInline: false,
  },
  flat_by_schedule: {
    key: 'flat_by_schedule',
    scopable: true,
    label: 'Flat by Schedule',
    description:
      'Be flat by a deadline — a daily time, or the instrument\'s own session close — and stay out until the window ends.',
    category: 'trading_restrictions',
    requiresValue: false,
    defaultValueMode: null,
    validValueModes: [],
    defaultTiming: 'scheduled',
    defaultAction: 'close_positions',
    hasParams: true,
    implemented: true,
    common: false,
    coreInline: false,
  },
  news_trading: {
    key: 'news_trading',
    label: 'News Trading',
    description: 'Block orders around high-impact economic events.',
    availabilityNote: 'Needs economic calendar',
    category: 'trading_restrictions',
    requiresValue: false,
    defaultValueMode: null,
    validValueModes: [],
    defaultTiming: 'pre_trade',
    defaultAction: 'reject_order',
    hasParams: true,
    implemented: true,
    common: false,
    coreInline: false,
  },
  instrument_whitelist: {
    key: 'instrument_whitelist',
    label: 'Instrument Whitelist',
    description: 'Only allow trading specific symbols.',
    category: 'trading_restrictions',
    requiresValue: false,
    defaultValueMode: null,
    validValueModes: [],
    defaultTiming: 'pre_trade',
    defaultAction: 'reject_order',
    hasParams: true,
    implemented: true,
    common: false,
    coreInline: false,
  },
  instrument_blacklist: {
    key: 'instrument_blacklist',
    label: 'Instrument Blacklist',
    description: 'Block trading specific symbols.',
    category: 'trading_restrictions',
    requiresValue: false,
    defaultValueMode: null,
    validValueModes: [],
    defaultTiming: 'pre_trade',
    defaultAction: 'reject_order',
    hasParams: true,
    implemented: true,
    common: false,
    coreInline: false,
  },

  // ── Protective orders ──
  // Enforced pre-trade: an order that OPENS exposure is judged on the stop and
  // target it carries. A pure close or reduce is exempt — a trader may always
  // take risk off — so none of these three can block an exit.
  mandatory_stop_loss: {
    key: 'mandatory_stop_loss',
    label: 'Mandatory Stop-Loss',
    description:
      'Orders that open exposure must carry a stop-loss. Closing orders are exempt.',
    category: 'trading_restrictions',
    requiresValue: false,
    defaultValueMode: null,
    validValueModes: [],
    defaultTiming: 'pre_trade',
    defaultAction: 'reject_order',
    hasParams: false,
    implemented: true,
    common: true,
    coreInline: false,
  },
  max_stop_loss_distance: {
    key: 'max_stop_loss_distance',
    label: 'Max Stop-Loss Distance',
    description:
      'How far the stop-loss may sit from the entry price — in price units, or as a percent of entry.',
    category: 'trading_restrictions',
    requiresValue: true,
    defaultValueMode: 'percent',
    validValueModes: ['percent', 'absolute'],
    defaultTiming: 'pre_trade',
    defaultAction: 'reject_order',
    hasParams: false,
    implemented: true,
    common: false,
    coreInline: false,
  },
  max_stop_target_ratio: {
    key: 'max_stop_target_ratio',
    label: 'Max Risk/Reward Ratio',
    description:
      'How much a trade may risk against what it stands to make. Orders under this rule must carry a take-profit.',
    category: 'trading_restrictions',
    requiresValue: true,
    defaultValueMode: 'absolute',
    validValueModes: ['absolute'],
    defaultTiming: 'pre_trade',
    defaultAction: 'reject_order',
    hasParams: false,
    implemented: true,
    common: false,
    coreInline: false,
  },

  // ── Intended risk ──
  // Enforced pre-trade on what an order PLANS to lose: its size across the gap
  // between its entry and its stop-loss. Distinct from `max_risk_per_trade`,
  // which is continuous and measures what an open position is DOWN right now.
  // A percent limit is a share of the attempt's starting balance, so the number
  // means the same thing on every order of the attempt.
  max_intended_risk_per_trade: {
    key: 'max_intended_risk_per_trade',
    label: 'Max Risk At Entry',
    description:
      "The most a trade may plan to lose — its size times the distance to its stop-loss. Pre-trade it judges the order arriving; continuous it keeps judging the position afterwards, which catches a stop widened once the trade is live. Percent is a share of the account's starting balance.",
    category: 'trading_restrictions',
    requiresValue: true,
    defaultValueMode: 'percent',
    validValueModes: ['percent', 'absolute'],
    defaultTiming: 'pre_trade',
    defaultAction: 'reject_order',
    hasParams: false,
    implemented: true,
    common: true,
    coreInline: false,
  },
  max_aggregate_intended_risk: {
    key: 'max_aggregate_intended_risk',
    label: 'Max Total Risk At Entry',
    description:
      'The same measure across every open position, plus the new order when judged pre-trade. A position without a stop-loss makes the total unmeasurable and fails closed.',
    category: 'trading_restrictions',
    requiresValue: true,
    defaultValueMode: 'percent',
    validValueModes: ['percent', 'absolute'],
    defaultTiming: 'pre_trade',
    defaultAction: 'reject_order',
    hasParams: false,
    implemented: true,
    common: false,
    coreInline: false,
  },

  // ── The continuous companion ──
  // The two intended-risk entries above are authorable in EITHER lane —
  // `valid_timings` accepts pre-trade and continuous for both — so they need no
  // second entry here; the timing is chosen per authored rule. This one has no
  // pre-trade form: there is no order to judge, only positions that may have
  // lost their stop after admission.
  stop_loss_required: {
    key: 'stop_loss_required',
    label: 'Stop Loss Required (Open Positions)',
    description:
      'Every open position must keep a stop-loss. Catches a stop removed after the position opened, which no pre-trade check can see.',
    category: 'trading_restrictions',
    requiresValue: false,
    defaultValueMode: null,
    validValueModes: [],
    defaultTiming: 'continuous',
    defaultAction: 'breach_account',
    hasParams: false,
    implemented: true,
    common: true,
    coreInline: false,
  },

  // ── Not yet implemented (defined in enum, no compiler/enforcement) ──
  copy_trading: {
    key: 'copy_trading',
    label: 'Copy Trading Detection',
    description: 'Detect and flag copy trading behavior.',
    category: 'trading_restrictions',
    requiresValue: false,
    defaultValueMode: null,
    validValueModes: [],
    defaultTiming: 'pre_trade',
    defaultAction: 'flag_for_review',
    hasParams: false,
    implemented: false,
    common: false,
    coreInline: false,
  },
  martingale: {
    key: 'martingale',
    label: 'Martingale Detection',
    description: 'Detect and flag martingale (doubling-down) patterns.',
    category: 'trading_restrictions',
    requiresValue: false,
    defaultValueMode: null,
    validValueModes: [],
    defaultTiming: 'pre_trade',
    defaultAction: 'flag_for_review',
    hasParams: false,
    implemented: false,
    common: false,
    coreInline: false,
  },
  hedging: {
    key: 'hedging',
    label: 'Hedging Detection',
    description: 'Detect and flag hedging strategies.',
    category: 'trading_restrictions',
    requiresValue: false,
    defaultValueMode: null,
    validValueModes: [],
    defaultTiming: 'pre_trade',
    defaultAction: 'flag_for_review',
    hasParams: false,
    implemented: false,
    common: false,
    coreInline: false,
  },
  max_daily_trades: {
    key: 'max_daily_trades',
    label: 'Max Trades Per Day',
    description:
      'How many orders that OPEN exposure may be placed in one day (from the day boundary). Closing orders never count and are never blocked.',
    category: 'trading_restrictions',
    requiresValue: true,
    defaultValueMode: 'absolute',
    validValueModes: ['absolute'],
    defaultTiming: 'pre_trade',
    defaultAction: 'reject_order',
    hasParams: false,
    implemented: true,
    common: true,
    coreInline: false,
  },
  min_hold_time: {
    key: 'min_hold_time',
    label: 'Min Hold Time',
    description: 'Minimum time a position must be held before closing.',
    category: 'trading_restrictions',
    requiresValue: true,
    defaultValueMode: 'seconds',
    validValueModes: ['seconds'],
    defaultTiming: 'continuous',
    defaultAction: 'flag_for_review',
    hasParams: false,
    implemented: false,
    common: false,
    coreInline: false,
  },
};

// ── Ordered lists for UI rendering ───────────────────────────────────

export function rulesForCategory(category: RuleCategory): RuleTypeConfig[] {
  return Object.values(RULE_REGISTRY).filter((r) => r.category === category);
}

// Rules shown in the StepRulesEditor toggle list — excludes coreInline rules
// (max_daily_loss, max_total_drawdown, max_trailing_drawdown) because those
// are controlled by dedicated inline fields in the step's core settings.
// Showing them as toggles too would let the user enable conflicting rules
// (e.g., both total AND trailing drawdown) which no prop firm does.

/** Common toggle rules — shown by default in the step rules editor. */
export function commonRulesForCategory(category: RuleCategory): RuleTypeConfig[] {
  return rulesForCategory(category).filter((r) => r.common && !r.coreInline);
}

/** Advanced toggle rules — behind an "Advanced" toggle. */
export function advancedRulesForCategory(category: RuleCategory): RuleTypeConfig[] {
  return rulesForCategory(category).filter((r) => !r.common && !r.coreInline);
}

// ── Default state factory ────────────────────────────────────────────

// ── Rule scope ───────────────────────────────────────────────────────
//
// The bucket a rule measures over, in exactly the tagged shape the backend
// stores and the compiler reads. A list of symbols is one INDEPENDENT bucket
// per symbol; a named group is ONE aggregate bucket. That difference is the
// whole reason both exist, and it is what the picker has to say out loud.

/** A named group's authored membership.
 *
 *  `members` is AUTHORING vocabulary, not wire vocabulary: the executable
 *  bucket is `{ id, revision }` and nothing else, and the membership travels
 *  beside the rules as the template's pinned catalogue. A group whose members
 *  are not pinned cannot be published — the operator would have sold a limit
 *  that measures nothing — so this is where they are stated. */
export interface SymbolGroupScope {
  id: string;
  revision: number;
  members: string[];
}

export type RuleScope =
  | { kind: 'account' }
  | { kind: 'symbols'; value: string[] }
  | { kind: 'asset_class'; value: string }
  | { kind: 'symbol_group'; value: SymbolGroupScope };

export const ACCOUNT_SCOPE: RuleScope = { kind: 'account' };

/** Asset classes the backend enum accepts, in its own order. */
export const ASSET_CLASSES = [
  'crypto',
  'forex',
  'indices',
  'commodities',
  'equities',
  'futures',
] as const;

export const SCOPE_KINDS: Array<{ value: RuleScope['kind']; label: string }> = [
  { value: 'account', label: 'Whole account' },
  { value: 'symbols', label: 'Individual symbols' },
  { value: 'asset_class', label: 'Asset class' },
  { value: 'symbol_group', label: 'Named group' },
];

export function isAccountScope(scope: RuleScope): boolean {
  return scope.kind === 'account';
}

/** Human wording for one bucket, including the independent-bucket reading. */
export function describeScope(scope: RuleScope): string {
  switch (scope.kind) {
    case 'account':
      return 'Measured across the whole account.';
    case 'symbols':
      return scope.value.length === 1
        ? `Measured on ${scope.value[0]}.`
        : `Measured separately on each of ${scope.value.join(', ')} — the limit applies to every one of them independently, not to their total.`;
    case 'asset_class':
      return `Measured across every ${scope.value} instrument together.`;
    case 'symbol_group':
      return scope.value.members.length === 0
        ? `Measured across the group's members together, at revision ${scope.value.revision} — no member is pinned yet, so this rule cannot be published.`
        : `Measured across ${scope.value.members.join(', ')} together, at revision ${scope.value.revision}.`;
  }
}

export interface RuleBuilderState {
  /** Stable row identity. Rows are keyed by this, not by rule type, because a
   *  scopable type may carry several rows — one per bucket. */
  id: string;
  enabled: boolean;
  ruleType: string;
  scope: RuleScope;
  /** The rung the first occurrence costs — the whole of what used to be an
   *  enforcement mode AND an action. */
  action: string;
  repeats: Array<[number, string]>;
  timing: string;
  value: string;
  valueMode: string;
  warningThresholdPct: string;
  params: Record<string, unknown>;
}

/** An enabled row planned against the economic calendar: its step must name
 *  the calendar source, and the save refuses until it does. */
export function needsCalendarSource(rule: RuleBuilderState): boolean {
  return rule.enabled && (rule.ruleType === 'news_trading'
    || (rule.ruleType === 'flat_by_schedule' && ['session_close', 'before_calendar_event'].includes(String(rule.params.boundary))));
}

let nextRuleRowOrdinal = 0;

/** A fresh row identity. Local to the editor: the backend mints the rule id. */
export function newRuleRowId(ruleType: string): string {
  nextRuleRowOrdinal += 1;
  return `${ruleType}#${nextRuleRowOrdinal}`;
}

export function defaultRuleState(config: RuleTypeConfig): RuleBuilderState {
  return {
    id: newRuleRowId(config.key),
    enabled: false,
    ruleType: config.key,
    scope: ACCOUNT_SCOPE,
    action: config.defaultAction,
    repeats: [],
    timing: config.defaultTiming,
    value: '',
    valueMode: config.defaultValueMode ?? '',
    warningThresholdPct: '80',
    params: {},
  };
}

/** A second, SCOPED row for a rule type that already has one. */
export function scopedRuleState(config: RuleTypeConfig, scope: RuleScope): RuleBuilderState {
  return { ...defaultRuleState(config), enabled: true, scope };
}

/** Initialize a full rules array with all rule types disabled. */
export function initializeAllRules(): RuleBuilderState[] {
  return Object.values(RULE_REGISTRY).map(defaultRuleState);
}

/** Pre-enable common rules for a standard challenge step. */
export function enableStandardRules(rules: RuleBuilderState[]): RuleBuilderState[] {
  return rules.map((r) => {
    if (r.ruleType === 'max_daily_loss') {
      return {
        ...r,
        enabled: true,
        value: '5',
        valueMode: 'percent',
        params: { ...r.params, commission_inclusive: true },
      };
    }
    if (r.ruleType === 'max_total_drawdown') {
      return {
        ...r,
        enabled: true,
        value: '10',
        valueMode: 'percent',
        params: { ...r.params, commission_inclusive: true },
      };
    }
    return r;
  });
}
